import { FC, useEffect, useState } from "react";
import {
  Box,
  FormHelperText,
  Grid,
  Stack,
} from "@mui/material";
import axios from "axios";
import dayjs from "dayjs";
import moment from "moment";
import { useFormik } from "formik";
import { v4 as uuidv4 } from 'uuid';
import toast from "react-hot-toast";
import AddVenueModalStyle from "./AddVenueModalStyle";
import { convertToISOFormat, isEndTimeValid, isVenueTimeInRange } from "../../utils/helper";
import InputFieldComponent from "../../components/inputField/InputFieldComponent";
import SelectDropdown from "../../components/selectDropdown/SelectDropdown";
import TimePickerComponent from "../../components/datePicker/TimePickerComponent";
import ButtonComponent from "../../components/button/ButtonComponent";
import AddVenueSchema from "./AddVenueSchema";
import { bookingType } from "../../utils/constant";
import { useTranslation } from "react-i18next";
interface AddExtraModelProps {
  handleClose: () => void;
  fetchVenue: () => void;
}

const AddVenueModal: FC<AddExtraModelProps> = ({
  handleClose,
  fetchVenue
}) => {
  const id = uuidv4()
  const { t } = useTranslation()
  const [isDisable, setIsDisable] = useState(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const initialValues: Venue = {
    name: "",
    maxBookingHoursPerYear: "",
    maxHourPerBooking: "",
    bookingType: "",
    start_time: "",
    end_time: "",
  };

  const formikVenue = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: AddVenueSchema(),
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values) => {
      setIsLoading(true)
      const payload = {
        ...values, id,
        start_time: dayjs(values.start_time).format("HH:mm"),
        end_time: dayjs(values.end_time).format("HH:mm")
      }
      try {
        const response = await axios.post("http://localhost:4200/venue", payload);
        if (response.status === 201) {
          toast.success(t("toastMsg.newVenue"))
          setIsLoading(false)
          fetchVenue()
          handleClose()
        } else {
          console.error("error", response.status);
          setIsLoading(false)
        }
      } catch (error) {
        console.error("error", error);
        setIsLoading(false)
      }
    },
  });

  useEffect(() => {
    const isTimeInRange: boolean | undefined = isVenueTimeInRange(formikVenue?.values?.start_time, formikVenue?.values?.end_time, formikVenue?.values?.maxHourPerBooking);

    if (isTimeInRange !== undefined) {
      if (isTimeInRange) {
        setIsDisable(false);
      } else {
        setIsDisable(true);
        toast.error(t("toastMsg.hrsRange", { time: formikVenue?.values?.maxHourPerBooking }));
      }
    }
  }, [formikVenue?.values])

  return (
    <Box>
      <Grid container {...AddVenueModalStyle.extraProductModelMainGrid}>
        <Grid item sm={12} md={12} lg={12} width={"100%"}>
          <form
            onSubmit={formikVenue.handleSubmit}
            className="tab-mobile-wrap tab-mobile-wrap-table"
          >
            <Stack spacing={2}>
              <InputFieldComponent
                label={t("addVenueModal.name")}
                placeHolder=""
                {...formikVenue.getFieldProps("name")}
                error={
                  formikVenue.touched.name &&
                  Boolean(formikVenue.errors.name)
                }
                helperText={
                  formikVenue.touched.name && formikVenue.errors.name
                }
              />
              <SelectDropdown
                label={t("addVenueModal.bookingType")}
                optionsData={bookingType}
                placeholder=""
                isSearchable={false}
                name="bookingType"
                onChange={(valuesSelect: BookingType) => {
                  formikVenue.setFieldValue(
                    "bookingType",
                    valuesSelect.value
                  );
                }}
                error={
                  formikVenue.touched.bookingType &&
                  Boolean(formikVenue.errors.bookingType)
                }
                helperText={
                  formikVenue.touched.bookingType &&
                  formikVenue.errors.bookingType
                }
                value={bookingType?.find(
                  (selected: BookingType) =>
                    selected?.value?.toString()?.toLocaleLowerCase() ===
                    formikVenue?.values?.bookingType
                      ?.toString()
                      ?.toLocaleLowerCase()
                )}
              />
              <InputFieldComponent
                label={t("addVenueModal.maxBookingHrs")}
                placeHolder=""
                {...formikVenue.getFieldProps("maxBookingHoursPerYear")}
                error={
                  formikVenue.touched.maxBookingHoursPerYear &&
                  Boolean(formikVenue.errors.maxBookingHoursPerYear)
                }
                helperText={
                  formikVenue.touched.maxBookingHoursPerYear &&
                  formikVenue.errors.maxBookingHoursPerYear
                }
              />
              <InputFieldComponent
                label={t("addVenueModal.maxHrsPerBooking")}
                labelSmall={"(Max 23hrs)"}
                placeHolder=""
                {...formikVenue.getFieldProps("maxHourPerBooking")}
                onChange={(e) => {
                  formikVenue.setFieldValue("maxHourPerBooking", e.target.value);
                  formikVenue.setFieldValue("start_time", "");
                  formikVenue.setFieldValue("end_time", "");
                }}
                error={
                  formikVenue.touched.maxHourPerBooking &&
                  Boolean(formikVenue.errors.maxHourPerBooking)
                }
                helperText={
                  formikVenue.touched.maxHourPerBooking &&
                  formikVenue.errors.maxHourPerBooking
                }
              />
              <Stack {...AddVenueModalStyle.radioGroupWrap}>
                <Grid item md={12} xs={12} sm={6} lg={6}>
                  <TimePickerComponent
                    labelText={t("addVenueModal.startTime")}
                    disabled={!moment().toString()}
                    value={
                      formikVenue.values?.start_time
                        ? dayjs(formikVenue.values?.start_time)
                        : null
                    }
                    slotProps={{
                      textField: {
                        onBlur:
                          formikVenue.getFieldProps("start_time").onBlur,
                      },
                    }}
                    onChange={(option) => {
                      const time = dayjs(option).format("HH:mm")
                      if (time === dayjs(formikVenue.values.end_time).format
                        ("HH:mm")) {
                        toast.error(t("toastMsg.sameTime"))
                        formikVenue.setFieldValue("end_time", "")
                      }
                      if (option && option.isValid()) {
                        formikVenue.setFieldValue(
                          "start_time",
                          option.format("YYYY-MM-DD HH:mm")
                        );
                        formikVenue.setFieldValue(
                          "end_time",
                          ""
                        );
                      }
                    }}
                    disableIgnoringDatePartForTimeValidation
                  />
                  {formikVenue.touched.start_time &&
                    (Boolean(formikVenue.errors.start_time) ? (
                      <FormHelperText
                        {...AddVenueModalStyle.startDateFormHelperText}
                      >
                        {formikVenue.errors.start_time
                          ? formikVenue.errors.start_time.toString()
                          : null}
                      </FormHelperText>
                    ) : null)}
                </Grid>
                <Grid
                  item
                  md={12}
                  xs={12}
                  sm={6}
                  lg={6}
                  {...AddVenueModalStyle.radioStackGrid}
                >
                  <TimePickerComponent
                    labelText={t("addVenueModal.endTime")}
                    disabled={!moment().toString()}
                    slotProps={{
                      textField: {
                        onBlur: formikVenue.getFieldProps("end_time").onBlur,
                      },
                    }}
                    value={
                      formikVenue.values?.end_time
                        ? dayjs(formikVenue.values?.end_time)
                        : null
                    }
                    onChange={(option) => {
                      const time = dayjs(option).format("HH:mm")
                      if (time === dayjs(formikVenue.values.start_time).format
                        ("HH:mm")) {
                        toast.error(t("toastMsg.sameTime"))
                        setIsDisable(true)
                        return
                      }
                      if (option && option.isValid()) {
                        formikVenue.setFieldValue(
                          "end_time",
                          option.format("YYYY-MM-DD HH:mm")
                        );
                      }
                    }}
                    disableIgnoringDatePartForTimeValidation
                  />
                  {formikVenue.touched.end_time &&
                    (Boolean(formikVenue.errors.end_time) ? (
                      <FormHelperText
                        {...AddVenueModalStyle.startDateFormHelperText}
                      >
                        {formikVenue.errors.end_time
                          ? formikVenue.errors.end_time.toString()
                          : null}
                      </FormHelperText>
                    ) : null)}
                  {isEndTimeValid(
                    //@ts-ignore
                    convertToISOFormat(formikVenue.values?.start_time),
                    formikVenue.values?.end_time
                  ) !== undefined &&
                    //@ts-ignore
                    isEndTimeValid(
                      //@ts-ignore
                      convertToISOFormat(formikVenue.values?.start_time),
                      formikVenue.values?.end_time
                    ) === false ? (
                    <FormHelperText
                      {...AddVenueModalStyle.startDateFormHelperText}
                    >
                      {isEndTimeValid(
                        //@ts-ignore
                        convertToISOFormat(formikVenue.values?.start_time),
                        formikVenue.values?.end_time
                      ) !== undefined &&
                        //@ts-ignore
                        isEndTimeValid(
                          //@ts-ignore
                          convertToISOFormat(formikVenue.values?.start_time),
                          formikVenue.values?.end_time
                        ) === false
                        ? "Time required"
                        : null}
                    </FormHelperText>
                  ) : null}
                </Grid>
              </Stack>
            </Stack>

            <Stack {...AddVenueModalStyle.promoCodeModelBtnStack}>
              <ButtonComponent
                text={t("btnText.cancel")}
                variant="outlined"
                onClick={handleClose}
                sx={{
                  ...AddVenueModalStyle.promoCodeModelBtnCancelSx,
                }}
              />
              <ButtonComponent
                text={t("btnText.add")}
                sx={{
                  ...AddVenueModalStyle.promoCodeModelBtnUpdateSx,
                }}
                type="submit"
                disabled={isLoading || isDisable}
              />
            </Stack>
          </form>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AddVenueModal;
